import assetTypeService from "../../../../services/assetService/assetTypeService";
import { CACHE_KEY_ASSETTYPES } from "../../../../utils/constants";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AssetTypeInterface } from "../../../../models/AssetModel/AssetType";

interface UpdateAssetTypesContext {
  previousAssetTypes: AssetTypeInterface[];
}

const useUpdateAssetType = (page: number, pageSize: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    AssetTypeInterface,
    Error,
    AssetTypeInterface,
    UpdateAssetTypesContext
  >({
    mutationFn: async (category: AssetTypeInterface) => {
      // Assuming the ID is stored in the category object or can be derived
      const { id, ...categoryData } = category;
      return assetTypeService.Update(id || "", categoryData);
    },

    onMutate: async (updatedAssetType: AssetTypeInterface) => {
      await queryClient.cancelQueries({
        queryKey: [
          CACHE_KEY_ASSETTYPES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
      });

      const previousAssetTypes =
        queryClient.getQueryData<AssetTypeInterface[]>([
          CACHE_KEY_ASSETTYPES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ]) || [];

      queryClient.setQueryData<AssetTypeInterface[]>(
        [
          CACHE_KEY_ASSETTYPES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        (assetTypes = []) =>
          assetTypes.map((assetType) =>
            assetType.id === updatedAssetType.id ? updatedAssetType : assetType
          )
      );

      return { previousAssetTypes };
    },

    onSuccess: (savedAssetType) => {
      queryClient.setQueryData<AssetTypeInterface[]>(
        [
          CACHE_KEY_ASSETTYPES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        (assetTypes) =>
          assetTypes?.map((assetType) =>
            assetType.id === savedAssetType.id ? savedAssetType : assetType
          )
      );
    },

    onError: (error, updatedAssetType, context) => {
      if (!context) return;

      queryClient.setQueryData<AssetTypeInterface[]>(
        [
          CACHE_KEY_ASSETTYPES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        context.previousAssetTypes
      );
    },
  });
};

export default useUpdateAssetType;
